import { Box, Card, CardContent, Chip, Link, Paper, Stack, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { t } from 'i18next';
import moment from 'moment';
import { Severity } from '../../../../common/enums/Severity';
import { DateUtils } from '../../../../common/utils/DateUtils';
import { UrlHelper } from '../../../../common/utils/UrlHelper';
import { PATHS } from '../../../../routing/routes';
import { OrganizationExportOptions } from '../../../options/OrganizationExportOptions';
import { Header, HeaderItem } from '../../common/components/Header';
import { HiddenIssues } from '../../common/components/HiddenIssues';
import { Inventory } from '../../common/components/Inventory';
import { BasePrintController, BasePrintControllerProps, BasePrintControllerState } from '../../common/controllers/BasePrintController';
import { TABLE_LAYOUT_SX } from '../../constants/constants';
import { IssueGroup } from '../components/IssueGroup';
import { OrganizationPrintData } from '../models/OrganizationPrintData';
import { OrganizationPrintRepository } from '../services/OrganizationPrintRepository';
import { TrendsOverview } from '../components/TrendsOverview';
import { formatRange } from '../../../../organization/dashboard/utils';
import { TrendDataScale } from '../../../../common/dashboards/models/TrendDataScale';
import { getLabel as getOverviewTypeLabel, OverviewType } from '../../../../common/dashboards/enums/OverviewType';

type Props = BasePrintControllerProps & {
  options: OrganizationExportOptions;
};

type State = BasePrintControllerState & {};

export class OrganizationPrintController extends BasePrintController<Props, State> {
  private readonly repository = new OrganizationPrintRepository();
  private organization: OrganizationPrintData | null = null;
  private organizationDetails: HeaderItem[] | null = null;

  componentDidMount(): void {
    this.fetchOrganization();
  }

  componentWillUnmount(): void {
    this.repository.cancelAllRequests();
  }

  render() {
    if (!this.organization || !this.organizationDetails) return <></>;

    const hasIssues = this.organization.hasPriorityIssues();
    const hasHiddenIssues = this.organization.hasHiddenIssues();
    const issueGroups = this.organization.getIssueGroups();
    const inventory = this.organization.getInventory();

    const hasInventory = inventory !== null && !inventory.isEmpty();
    const trends = this.props.options.trends;
    const includePassed = this.props.options.includePassed;

    return this.renderBase(
      <Box>
        <Box sx={{ mb: 2 }}>
          <Header title={this.organization.getName()} items={this.organizationDetails} />
        </Box>
        <Paper elevation={2} sx={{ mb: 2 }}>
          <Table data-print="table-layout" sx={TABLE_LAYOUT_SX}>
            <TableBody>
              <TableRow>
                <TableCell colSpan={2}>
                  <TrendsOverview trends={trends} includePassed={includePassed} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
        {hasInventory && (
          <Box sx={{ mb: 2 }}>
            <Inventory inventory={inventory} />
          </Box>
        )}
        {hasIssues && (
          <Card>
            <CardContent>
              <IssueGroup title={t('criticality.high')} issues={issueGroups[Severity.HIGH]} />
              <IssueGroup title={t('criticality.med')} issues={issueGroups[Severity.MED]} />
              <IssueGroup title={t('criticality.low')} issues={issueGroups[Severity.LOW]} />
              <IssueGroup title={t('criticality.passed')} issues={issueGroups[Severity.PASSED]} />
              {hasHiddenIssues && <HiddenIssues />}
            </CardContent>
          </Card>
        )}
        {!hasIssues && hasHiddenIssues && <HiddenIssues section />}
      </Box>
    );
  }

  private async fetchOrganization() {
    try {
      this.organization = await this.repository.getTrendsOrganization(this.props.options);

      const profiles = this.organization.getProfiles();
      const projects = this.organization.getProjects();
      const tags = this.organization.getTags();

      this.organizationDetails = [
        {
          label: t('generatedAt'),
          value: DateUtils.toDateTimeString(DateUtils.toMoment(moment.now())),
        },
      ];
      if (projects.length > 0) {
        this.organizationDetails.push({
          label: t('projects'),
          value: (
            <>
              {projects.map((p) => (
                <Link key={p.getId()} href={UrlHelper.generatePath(PATHS.PROJECT_DASHBOARD, { projectId: p.getId() })} sx={{ mb: 0.5, display: 'block' }}>
                  {p.getLabel()}
                </Link>
              ))}
            </>
          ),
        });
      }
      if (profiles.length > 0) {
        this.organizationDetails.push({
          label: t('profiles'),
          value: (
            <>
              {profiles.map((p) => (
                <Link
                  key={p.getId()}
                  href={UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: p.getId().toString() })}
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  {p.toFilterFormat(true).label}
                </Link>
              ))}
            </>
          ),
        });
      }
      if (tags.length > 0) {
        this.organizationDetails.push({
          label: t('tags'),
          value: (
            <Stack direction="row" flexWrap="wrap" spacing={1}>
              {tags.map((tag) => (
                <Chip key={tag} label={tag} sx={{ py: 0.25 }} />
              ))}
            </Stack>
          ),
        });
      }

      const dateRange = this.props.options.dateRange;
      // Add the date range
      this.organizationDetails.push({
        label: t('dateRange'),
        value: formatRange(dateRange, TrendDataScale.DAY, false),
      });

      // Add the exceptions
      this.organizationDetails.push({
        label: t('exceptions'),
        value: this.props.options.withExceptions ? t('withExceptions') : t('withoutExceptions'),
      });

      // Add the overview types
      let overviewTypes = this.props.options.overviews.map((overviewType) => getOverviewTypeLabel(overviewType as OverviewType));
      if (overviewTypes.length === 0) {
        overviewTypes.push(t('noOverviewTypesSelected'));
      } else if (overviewTypes.length === Object.values(OverviewType).length) {
        overviewTypes = [t('allOverviewTypes')];
      }
      this.organizationDetails.push({
        label: t('overviewTypes'),
        value: overviewTypes.join(', '),
      });

      this.onRendered();
    } catch (e: unknown) {
      this.onError(e);
    }
  }
}
